import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Scale, ArrowRight, ScanSearch, ListTree, FileSignature, ShieldCheck, Quote,
  Upload, Sparkles, FileCheck2, Check, FileText, Zap,
  Coins, Handshake, Gavel, BookOpen,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/lib/i18n";
import LanguageToggle from "@/components/LanguageToggle";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";

const HERO_IMG =
  "https://images.unsplash.com/photo-1505488387362-48bc38155987?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwzfHxkYXJrJTIwbGF3JTIwbGlicmFyeSUyMGNvbHVtbnMlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fGJsYWNrfDE3ODc0NTkwNTZ8MA&ixlib=rb-4.1.0&q=85";

const STEP = (n) => `LCA-${String(n).padStart(3, "0")}`;

const HERO_LEFT = { initial: { opacity: 0, y: 14 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6 } };
const HERO_RIGHT = { initial: { opacity: 0, y: 24, rotate: 1 }, animate: { opacity: 1, y: 0, rotate: 0 }, transition: { duration: 0.7, delay: 0.15 } };
const REVEAL = { initial: { opacity: 0, y: 12 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

const CASE_THEME = {
  rose: { badge: "border-rose-500/30 bg-rose-500/10 text-rose-300", dot: "bg-rose-400", bar: "bg-rose-400", text: "text-rose-300", iconText: "text-rose-400", recBg: "border-rose-500/25 bg-rose-500/[0.06]" },
  cyan: { badge: "border-cyan-500/30 bg-cyan-500/10 text-cyan-300", dot: "bg-cyan-400", bar: "bg-cyan-400", text: "text-cyan-300", iconText: "text-cyan-400", recBg: "border-cyan-500/25 bg-cyan-500/[0.06]" },
  indigo: { badge: "border-indigo-500/30 bg-indigo-500/10 text-indigo-300", dot: "bg-indigo-400", bar: "bg-indigo-400", text: "text-indigo-300", iconText: "text-indigo-400", recBg: "border-indigo-500/25 bg-indigo-500/[0.06]" },
  amber: { badge: "border-amber-500/30 bg-amber-500/10 text-amber-300", dot: "bg-amber-400", bar: "bg-amber-400", text: "text-amber-300", iconText: "text-amber-400", recBg: "border-amber-500/25 bg-amber-500/[0.06]" },
};

const CASE_EXAMPLES = [
  { theme: "rose", tone: "litigasi", key: "ex1", val: "Rp 500.000.000", conf: 85 },
  { theme: "cyan", tone: "mediasi", key: "ex2", val: "Rp 850.000.000", conf: 78 },
  { theme: "indigo", tone: "mediasi", key: "ex3", val: "Rp 200.000.000", conf: 71 },
  { theme: "amber", tone: "litigasi", key: "ex4", val: "Rp 3.200.000.000", conf: 92 },
];

/* ---------- Live product mockup ---------- */
function ProductMock() {
  const { t } = useI18n();
  const docket = [
    { c: STEP(4), t: t("landing.mockDocket1"), active: true, done: true },
    { c: STEP(5), t: t("landing.mockDocket2"), done: true },
    { c: STEP(7), t: t("landing.mockDocket3"), done: true },
    { c: STEP(9), t: t("landing.mockDocket4"), done: false },
  ];
  return (
    <div className="rounded-xl border border-zinc-800 bg-[#121216] shadow-2xl shadow-black/50 overflow-hidden">
      <div className="flex items-center gap-1.5 px-4 py-3 border-b border-zinc-800 bg-[#0a0a0c]/60">
        <span className="h-2.5 w-2.5 rounded-full bg-zinc-700" />
        <span className="h-2.5 w-2.5 rounded-full bg-zinc-700" />
        <span className="h-2.5 w-2.5 rounded-full bg-zinc-700" />
        <span className="ml-3 font-mono-ui text-[10px] text-zinc-500 truncate">PT Maju vs CV Sentosa · workspace</span>
      </div>
      <div className="grid grid-cols-[120px_1fr]">
        {/* docket */}
        <div className="border-r border-zinc-800 p-3 space-y-1">
          <p className="font-mono-ui text-[8px] uppercase tracking-widest text-zinc-600 px-1">SOP 60%</p>
          <div className="h-1 rounded-full bg-zinc-800 overflow-hidden mb-2">
            <div className="h-full w-[60%] bg-[#d4af37]" />
          </div>
          {docket.map((d) => (
            <div key={d.c}
              className={`rounded-md px-2 py-1.5 border-l-2 ${d.active ? "border-[#d4af37] bg-zinc-800/60" : "border-transparent"}`}>
              <div className="flex items-center gap-1">
                {d.done ? <Check className="h-2.5 w-2.5 text-emerald-400" strokeWidth={3} />
                  : <span className="h-2 w-2 rounded-full border border-zinc-700" />}
                <span className="font-mono-ui text-[8px] text-zinc-500">{d.c}</span>
              </div>
              <p className="mt-0.5 text-[10px] leading-tight text-zinc-300">{d.t}</p>
            </div>
          ))}
        </div>
        {/* item */}
        <div className="p-4 space-y-3">
          <div className="rounded-lg border border-zinc-800 bg-[#18181d] p-3">
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center gap-1 rounded border border-cyan-500/30 bg-cyan-500/10 px-1.5 py-0.5 font-mono-ui text-[8px] uppercase tracking-wider text-cyan-300">
                <span className="h-1 w-1 rounded-full bg-cyan-400" /> {t("cat.Fakta")}
              </span>
              <span className="font-mono-ui text-[8px] text-zinc-600">AI</span>
            </div>
            <p className="mt-2 text-[11px] leading-relaxed text-zinc-200">
              {t("landing.mockFact")}
            </p>
            <div className="mt-2 inline-flex items-center gap-1 rounded-md border border-zinc-700 bg-zinc-800/50 px-1.5 py-1">
              <FileText className="h-2.5 w-2.5 text-zinc-500" />
              <span className="font-mono-ui text-[9px] text-zinc-300">gugatan.pdf · {t("chip.page", { n: 1 })}</span>
            </div>
          </div>
          {/* source popover */}
          <div className="rounded-lg border border-[#d4af37]/30 bg-[#0a0a0c] p-3">
            <p className="font-mono-ui text-[8px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
              <Quote className="h-2.5 w-2.5" /> {t("landing.mockQuote")}
            </p>
            <p className="mt-1.5 text-[11px] italic leading-relaxed text-zinc-300 border-l-2 border-[#d4af37]/60 pl-2">
              “…menandatangani Perjanjian Jual Beli Barang Nomor 015/PJB/2024 senilai Rp500.000.000…”
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Landing() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const cta = () => nav(user ? "/dashboard" : "/login?mode=register");

  const FAQS = [1, 2, 3, 4, 5, 6].map((i) => ({ q: t(`landing.faq${i}q`), a: t(`landing.faq${i}a`) }));
  const TESTIMONIALS = [1, 2, 3, 4, 5].map((i) => ({
    initials: ["RP", "SW", "DP", "FH", "MN"][i - 1],
    name: t(`landing.t${i}name`), role: t(`landing.t${i}role`), quote: t(`landing.t${i}quote`),
  }));
  const STATS = [
    { n: "15", l: t("landing.stat1") },
    { n: "5", l: t("landing.stat2") },
    { n: "~min", l: t("landing.stat3") },
    { n: "PDF · DOCX", l: t("landing.stat4") },
  ];
  const FEATURES = [
    { icon: ScanSearch, title: t("landing.feat1t"), desc: t("landing.feat1d") },
    { icon: ListTree, title: t("landing.feat2t"), desc: t("landing.feat2d") },
    { icon: FileSignature, title: t("landing.feat3t"), desc: t("landing.feat3d") },
    { icon: ShieldCheck, title: t("landing.feat4t"), desc: t("landing.feat4d") },
  ];
  const STEPS = [
    { n: "01", icon: Upload, title: t("landing.step1t"), desc: t("landing.step1d") },
    { n: "02", icon: Sparkles, title: t("landing.step2t"), desc: t("landing.step2d") },
    { n: "03", icon: FileCheck2, title: t("landing.step3t"), desc: t("landing.step3d") },
  ];

  return (
    <div className="min-h-screen">
      {/* Nav */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#0a0a0c]/80 border-b border-white/5">
        <div className="mx-auto max-w-6xl px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Scale className="h-5 w-5 text-[#d4af37]" strokeWidth={1.5} />
            <span className="font-mono-ui text-sm tracking-[0.25em] uppercase text-zinc-100">Lexiqa</span>
          </div>
          <div className="flex items-center gap-3">
            <LanguageToggle />
            <a data-testid="nav-manual-link"
              href={`${process.env.REACT_APP_BACKEND_URL}/api/manual.pdf`}
              target="_blank" rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-zinc-100 transition-colors">
              <BookOpen className="h-4 w-4" /> {t("common.guide")}
            </a>
            <Link to="/login" data-testid="nav-login-link"
              className="hidden sm:inline text-sm text-zinc-400 hover:text-zinc-100 transition-colors">{t("common.login")}</Link>
            <Button data-testid="nav-cta-btn" onClick={cta}
              className="h-9 rounded-full bg-zinc-50 text-zinc-950 hover:bg-white px-4">
              {t("common.startAnalysis")} <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-zinc-800/70">
        <img src={HERO_IMG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-[0.14]" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0c] via-[#0a0a0c]/80 to-[#0a0a0c]/40" />
        <div className="relative mx-auto max-w-6xl px-6 py-20 sm:py-28 grid lg:grid-cols-[1.05fr_0.95fr] gap-12 items-center">
          <motion.div {...HERO_LEFT}>
            <div className="inline-flex items-center gap-2 rounded-full border border-zinc-700/70 bg-[#121216]/60 px-3 py-1 font-mono-ui text-[11px] uppercase tracking-[0.15em] text-zinc-400">
              <span className="h-1.5 w-1.5 rounded-full bg-[#d4af37]" /> {t("landing.badge")}
            </div>
            <h1 className="mt-6 font-serif-display text-4xl sm:text-6xl font-light leading-[1.05] text-zinc-50">
              {t("landing.heroA")}
              <br />
              <span className="italic text-zinc-300">{t("landing.heroB")}</span>{t("landing.heroC")}
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-zinc-400">
              {t("landing.heroSub")}
            </p>
            <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-zinc-500">
              {t("landing.heroDesc")}
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Button data-testid="hero-cta-btn" onClick={cta}
                className="cta-glow h-12 rounded-full bg-zinc-50 text-zinc-950 hover:bg-white px-7 text-base group">
                {t("common.startAnalysis")}
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Button>
              <span className="font-mono-ui text-[11px] text-zinc-600 tracking-wider">
                {STEP(1)} → {STEP(15)} · SOURCE-GROUNDED
              </span>
            </div>
          </motion.div>
          <motion.div {...HERO_RIGHT} className="hidden lg:block">
            <ProductMock />
          </motion.div>
        </div>

        {/* stats */}
        <div className="relative border-t border-zinc-800/70 bg-[#0a0a0c]/60">
          <div className="mx-auto max-w-6xl px-6 grid grid-cols-2 sm:grid-cols-4 divide-x divide-zinc-800/70">
            {STATS.map((s) => (
              <div key={s.l} className="px-4 py-6">
                <p className="font-serif-display text-2xl sm:text-3xl font-light text-[#d4af37]">{s.n}</p>
                <p className="mt-1 font-mono-ui text-[10px] uppercase tracking-wider text-zinc-500">{s.l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cara Kerja */}
      <section className="mx-auto max-w-6xl px-6 py-20">
        <p className="font-mono-ui text-[11px] uppercase tracking-[0.15em] text-zinc-500">{t("landing.howSmall")}</p>
        <h2 className="mt-2 font-serif-display text-3xl sm:text-4xl font-light text-zinc-50">
          {t("landing.howH2")}
        </h2>
        <div className="mt-10 grid md:grid-cols-3 gap-4">
          {STEPS.map((s, i) => (
            <motion.div key={s.n}
              {...REVEAL} transition={{ delay: i * 0.08 }}
              className="relative rounded-xl border border-[#2a2a35] bg-[#18181d] p-6 hover:border-zinc-600 transition-colors">
              <div className="flex items-center justify-between">
                <span className="font-serif-display text-4xl font-light text-zinc-700">{s.n}</span>
                <s.icon className="h-6 w-6 text-[#d4af37]" strokeWidth={1.5} />
              </div>
              <h3 className="mt-4 font-serif-display text-xl font-normal text-zinc-100">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-zinc-500">{s.desc}</p>
              {i < STEPS.length - 1 && (
                <ArrowRight className="hidden md:block absolute -right-3 top-1/2 h-5 w-5 text-zinc-700" />
              )}
            </motion.div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="mx-auto max-w-6xl px-6 pb-4">
        <p className="font-mono-ui text-[11px] uppercase tracking-[0.15em] text-zinc-500">{t("landing.featSmall")}</p>
        <h2 className="mt-2 font-serif-display text-3xl sm:text-4xl font-light text-zinc-50">
          {t("landing.featH2")}
        </h2>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {FEATURES.map((f, i) => (
            <motion.div key={f.title}
              {...REVEAL} transition={{ delay: i * 0.06 }}
              className="rounded-xl border border-[#2a2a35] bg-[#18181d] p-6 hover:border-zinc-600 transition-colors">
              <f.icon className="h-6 w-6 text-[#d4af37]" strokeWidth={1.5} />
              <h3 className="mt-4 font-serif-display text-xl font-normal text-zinc-100">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-zinc-500">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Grounding showcase */}
      <section className="mx-auto max-w-6xl px-6 py-20">
        <div className="rounded-2xl border border-zinc-800 bg-[#121216] p-8 sm:p-12 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-1.5 font-mono-ui text-[11px] uppercase tracking-[0.15em] text-[#d4af37]">
              <ShieldCheck className="h-4 w-4" /> {t("landing.groundBadge")}
            </div>
            <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl font-light leading-tight text-zinc-50">
              {t("landing.groundH2")}
            </h2>
            <p className="mt-5 text-[15px] leading-relaxed text-zinc-400">
              {t("landing.groundP")}
            </p>
            <ul className="mt-6 space-y-3">
              {[t("landing.groundB1"), t("landing.groundB2"), t("landing.groundB3")].map((txt) => (
                <li key={txt} className="flex items-start gap-2.5 text-sm text-zinc-300">
                  <Check className="mt-0.5 h-4 w-4 text-emerald-400 shrink-0" strokeWidth={2.5} /> {txt}
                </li>
              ))}
            </ul>
          </div>
          <div className="grid gap-3">
            <div className="rounded-lg border border-rose-500/20 bg-rose-500/[0.04] p-4">
              <p className="font-mono-ui text-[10px] uppercase tracking-wider text-rose-400/80">{t("landing.mockGeneric")}</p>
              <p className="mt-2 text-sm text-zinc-400">{t("landing.mockGenericQ")}</p>
            </div>
            <div className="flex justify-center"><Zap className="h-5 w-5 text-[#d4af37]" /></div>
            <div className="rounded-lg border border-emerald-500/25 bg-emerald-500/[0.05] p-4">
              <p className="font-mono-ui text-[10px] uppercase tracking-wider text-emerald-400/90">{t("landing.mockGrounded")}</p>
              <p className="mt-2 text-sm text-zinc-200">
                "Wanprestasi Rp300.000.000 akibat kekurangan 600 unit."
              </p>
              <div className="mt-2 inline-flex items-center gap-1 rounded-md border border-zinc-700 bg-zinc-800/50 px-2 py-1">
                <FileText className="h-3 w-3 text-zinc-500" />
                <span className="font-mono-ui text-[10px] text-zinc-300">gugatan.pdf · {t("chip.page", { n: 1 })}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contoh Hasil Analisis */}
      <section className="border-t border-zinc-800/70 bg-[#0b0b0e]">
        <div className="mx-auto max-w-6xl px-6 py-20">
          <p className="font-mono-ui text-[11px] uppercase tracking-[0.15em] text-[#d4af37]">{t("landing.examplesSmall")}</p>
          <h2 className="mt-2 font-serif-display text-3xl sm:text-4xl font-light text-zinc-50">
            {t("landing.examplesH2")}
          </h2>
          <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-zinc-500">
            {t("landing.examplesP")}
          </p>

          <div className="mt-10 grid sm:grid-cols-2 gap-4" data-testid="example-analysis">
            {CASE_EXAMPLES.map((c, i) => {
              const th = CASE_THEME[c.theme];
              const RecIcon = c.tone === "mediasi" ? Handshake : Gavel;
              return (
                <motion.div key={c.key} {...REVEAL} transition={{ delay: i * 0.08 }}
                  data-testid={`case-card-${i}`}
                  className="flex flex-col rounded-xl border border-zinc-800 bg-[#121216] p-6 hover:border-zinc-700 transition-colors">
                  <span className={`self-start inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono-ui text-[10px] uppercase tracking-wider ${th.badge}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${th.dot}`} /> {t(`landing.${c.key}badge`)}
                  </span>
                  <h3 className="mt-3 font-serif-display text-lg leading-snug text-zinc-100">{t(`landing.${c.key}jenis`)}</h3>
                  <div className="mt-2 flex items-center gap-2 text-sm">
                    <Coins className="h-4 w-4 text-zinc-500 shrink-0" strokeWidth={1.5} />
                    <span className="text-zinc-500">{t(`landing.${c.key}val`)}:</span>
                    <span className="text-zinc-100">{c.val}</span>
                  </div>

                  <div className={`mt-4 rounded-lg border p-3 ${th.recBg}`}>
                    <p className={`font-mono-ui text-[10px] uppercase tracking-wider ${th.text}`}>{t("landing.recLabel")}</p>
                    <p className="mt-1.5 flex items-center gap-2 text-[15px] text-zinc-50">
                      <RecIcon className={`h-4 w-4 shrink-0 ${th.iconText}`} strokeWidth={1.5} /> {t(`landing.${c.key}rec`)}
                    </p>
                  </div>

                  <div className="mt-4">
                    <div className="flex items-center justify-between">
                      <span className="font-mono-ui text-[10px] uppercase tracking-wider text-zinc-500">{t("landing.confLabel")}</span>
                      <span className={`font-mono-ui text-sm ${th.text}`}>{c.conf}%</span>
                    </div>
                    <div className="mt-1.5 h-1.5 rounded-full bg-zinc-800 overflow-hidden">
                      <motion.div initial={{ width: 0 }} whileInView={{ width: `${c.conf}%` }}
                        viewport={{ once: true }} transition={{ duration: 0.9, ease: "easeOut" }}
                        className={`h-full rounded-full ${th.bar}`} />
                    </div>
                  </div>

                  <p className="mt-4 text-sm leading-relaxed text-zinc-400">
                    <span className="font-mono-ui text-[10px] uppercase tracking-wider text-zinc-500">{t("landing.reasonLabel")} </span>
                    {t(`landing.${c.key}reason`)}
                  </p>
                </motion.div>
              );
            })}
          </div>

          <p className="mt-4 max-w-3xl font-mono-ui text-[11px] leading-relaxed text-zinc-600">
            {t("landing.examplesNote")}
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-zinc-800/70">
        <div className="mx-auto max-w-3xl px-6 py-20">
          <p className="font-mono-ui text-[11px] uppercase tracking-[0.15em] text-zinc-500">FAQ</p>
          <h2 className="mt-2 font-serif-display text-3xl sm:text-4xl font-light text-zinc-50">
            {t("landing.faqH2")}
          </h2>
          <Accordion type="single" collapsible className="mt-8" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem key={f.q} value={`faq-${i}`}
                data-testid={`faq-item-${i}`}
                className="border-zinc-800">
                <AccordionTrigger className="py-5 text-base text-zinc-100 hover:no-underline hover:text-[#d4af37] [&[data-state=open]]:text-[#d4af37]">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[15px] leading-relaxed text-zinc-400">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Testimoni Beta */}
      <section className="border-t border-zinc-800/70 bg-[#0b0b0e]">
        <div className="mx-auto max-w-6xl px-6 py-20">
          <div className="flex items-center gap-3">
            <p className="font-mono-ui text-[11px] uppercase tracking-[0.15em] text-[#d4af37]">{t("landing.testiSmall")}</p>
            <span className="rounded-full border border-zinc-700 px-2 py-0.5 font-mono-ui text-[10px] uppercase tracking-wider text-zinc-500">{t("landing.betaBadge")}</span>
          </div>
          <h2 className="mt-2 font-serif-display text-3xl sm:text-4xl font-light text-zinc-50">
            {t("landing.testiH2")}
          </h2>
          <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-zinc-500">
            {t("landing.testiP")}
          </p>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="testimonials">
            {TESTIMONIALS.map((tm, i) => (
              <motion.figure key={tm.name} {...REVEAL} transition={{ delay: i * 0.06 }}
                data-testid={`testimonial-${i}`}
                className="flex flex-col rounded-xl border border-zinc-800 bg-[#121216] p-6">
                <Quote className="h-5 w-5 text-[#d4af37]/70" strokeWidth={1.5} />
                <blockquote className="mt-3 flex-1 text-[15px] leading-relaxed text-zinc-300">
                  “{tm.quote}”
                </blockquote>
                <figcaption className="mt-5 flex items-center gap-3 border-t border-zinc-800 pt-4">
                  <span className="grid h-9 w-9 place-items-center rounded-full bg-[#d4af37]/15 font-mono-ui text-[11px] text-[#d4af37]">
                    {tm.initials}
                  </span>
                  <span>
                    <span className="block text-sm text-zinc-100">{tm.name}</span>
                    <span className="block font-mono-ui text-[11px] text-zinc-500">{tm.role}</span>
                  </span>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="relative overflow-hidden border-t border-zinc-800/70">
        <div className="mx-auto max-w-6xl px-6 py-24 text-center">
          <h2 className="font-serif-display text-3xl sm:text-5xl font-light text-zinc-50">
            {t("landing.ctaH2")}
          </h2>
          <p className="mt-4 text-zinc-500 max-w-lg mx-auto">
            {t("landing.ctaP")}
          </p>
          <Button data-testid="footer-cta-btn" onClick={cta}
            className="cta-glow mt-8 h-12 rounded-full bg-zinc-50 text-zinc-950 hover:bg-white px-8 text-base group">
            {t("common.startAnalysis")} <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Button>
        </div>
      </section>

      <footer className="border-t border-zinc-800/70">
        <div className="mx-auto max-w-6xl px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-zinc-500">
            <Scale className="h-4 w-4 text-[#d4af37]" strokeWidth={1.5} />
            <span className="font-mono-ui text-xs tracking-[0.2em] uppercase text-zinc-300">Lexiqa</span>
          </div>
          <p className="font-mono-ui text-[11px] text-zinc-600 text-center max-w-md">
            {t("landing.footerDisclaimer")}
          </p>
          <div className="flex items-center gap-4">
            <Link to="/privacy" data-testid="footer-privacy-link"
              className="font-mono-ui text-[11px] text-zinc-500 hover:text-zinc-200 transition-colors">
              {t("login.consentPrivacy")}
            </Link>
            <a data-testid="footer-manual-link"
              href={`${process.env.REACT_APP_BACKEND_URL}/api/manual.pdf`}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 font-mono-ui text-[11px] text-[#d4af37] hover:text-[#e6c04d] transition-colors">
              <BookOpen className="h-3.5 w-3.5" /> {t("common.manualDownload")}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
